// Copyright © 2015-2020 TrampleZone LLC All Rights Reserved

// EventUtils Module - support for managing any kind of event

define('EventUtils',['require'],function (require) {
    'use strict';

    var // Instance vars
        self,
        isTouchable,                    // True if device is touchable
        transitionEvent,                // Set transition event type once
        supportedEventTypes,            // Event types supported by this device
        supportsTouchAndClick = false;  // True if both touchable and clickable

    // Constructor
    function EventUtils () {
        // Preserve instance reference and enforce singleton
        if (typeof self === 'object') { return self; } else { self = this; }
    };

    // Public Methods

    // Direction enum
    EventUtils.prototype.direction = Object.freeze({
        LEFT: 0,
        RIGHT: 1,
        UP: 2,
        DOWN: 3
    });

    // Get the supported event types for this device
    EventUtils.prototype.getSupportedEventTypes = function () {

        // Determine supported event types, if not yet determined
        if (typeof supportedEventTypes === 'undefined') {

            // Initialize an array of supported event types
            supportedEventTypes = [];

            // Check for touchable devices (mobile or desktop)
            if (this.isTouchable()) {
                supportedEventTypes.push('ontouchstart');
                supportedEventTypes.push('ontouchend');
            }

            // Check for clickable devices
            if ('onclick' in document.documentElement) {
                supportedEventTypes.push('onclick');
            }

            // Check if device supports both touch and click
            if (supportedEventTypes.length > 2) {
                supportsTouchAndClick = true;
            }
        }

        // Return complete set of supported event types for device
        return supportedEventTypes;
    };

    // Add click or touch listeners based on device
    EventUtils.prototype.addEventListeners = function (element, callback) {
        // Callback handling
        var touchStartX,
            touchStartY,
            touched = false,
            delta;

        // Callback function when click or touch events are fired
        var eventHandler = function (event) {

            // Handle touch or click events
            switch (event.type) {

                case 'touchstart':
                    // Capture coordinates of where touch started
                    var touch = event.touches[0] || event.changedTouches[0];
                    touchStartX = touch.pageX;
                    touchStartY = touch.pageY;
                    break;

                case 'touchend':
                    // Capture coordinates of where touch ended
                    var touchEnd = event.touches[0] || event.changedTouches[0];

                    // Get the delta between start and end
                    delta = Math.abs(touchStartX - touchEnd.pageX) +
                            Math.abs(touchStartY - touchEnd.pageY);

                    // If the delta is within tolerance, consider it a click
                    if (delta <= 20) {

                        // Consume ghost clicks
                        if (supportsTouchAndClick) {
                            // Stop further handling of this event
                            event.preventDefault();

                            // Mark touched & reset after possible ghost click
                            touched = true;
                            setTimeout(function () {
                                touched = false;
                            }, 500);
                        }

                        // Event complete
                        callback(event);
                    }
                    break;

                case 'click':
                    // Call the callback
                    if (callback && !touched) {
                        callback(event);
                    }
                    break;
            }
        }.bind(this);

        // Assign listeners for supported events, click/touchstart/touchend
        if (typeof element != 'undefined') {

            // Get supported event types for this device
            if (typeof supportedEventTypes === 'undefined') {
                supportedEventTypes = this.getSupportedEventTypes();
            }

            // Assign event handler for each supported event type
            var eventTypesLength = supportedEventTypes.length;
            for (var i=0; i<eventTypesLength; i++) {
                element[supportedEventTypes[i]] = eventHandler;
            }
        }
    };

    // Get transition event for this platform
    EventUtils.prototype.getTransitionEvent = function () {
        if (transitionEvent === undefined) {
            var el = document.createElement('div');
            var transitions = {
                'transition'       :'transitionend',
                'WebkitTransition' :'webkitTransitionEnd',
                'MozTransition'    :'transitionend',
                'mSTransition'     :'msTransitionEnd',
                'OTransition'      :'oTransitionEnd'
            };
            for (var t in transitions) {
                if (el.style[t] !== undefined ) {
                    transitionEvent = transitions[t];
                    break;
                }
            }
        }
        return transitionEvent;
    };

    // Determine if device is touch enabled
    EventUtils.prototype.isTouchable = function () {
        // Only do the check once per session
        if (typeof isTouchable === 'undefined') {
            // Check for touch enabled device
            isTouchable = false;
            if ('ontouchstart' in window ||
                (window.DocumentTouch && document instanceof DocumentTouch)) {
                isTouchable = true;
            }
        }
        return isTouchable;
    };

    // Attach given event on the window object
    EventUtils.prototype.setWindowEventListener =
        function (eventType, callback) {
        if (window.addEventListener) {
            window.addEventListener(eventType, callback, false);
        } else {
            window.attachEvent(eventType, callback);
        }
    };

    // Add swipe listeners based on device
    EventUtils.prototype.addSwipeEventListeners = function (element, callback) {
        // Callback handling
        var swipeDirection = null,
            touchStartX,
            touchStartY,
            deltaX,
            deltaY,
            elapsedTime,
            startTime,
            threshold = 100,    // Min distance to be considered swipe
            allowedTime = 400,  // Max time allowed to travel that distance
            restraint = 200;    // Max perpendicular distance allowed

        // Add touch start listener
        element.addEventListener('touchstart', function(e) {
            var touchObj = e.changedTouches[0];
            deltaX = 0;
            deltaY = 0;
            touchStartX = touchObj.pageX;
            touchStartY = touchObj.pageY;
            // record time when finger first makes contact with surface
            startTime = new Date().getTime();
        }, false);

        // Add touch end listener
        element.addEventListener('touchend', function (e) {
            var touchObj = e.changedTouches[0];
            // get total dist traveled by finger while in contact with surface
            deltaX = touchObj.pageX - touchStartX;
            // get vertical dist traveled by finger while in contact with surface
            deltaY = touchObj.pageY - touchStartY;
            // get time elapsed
            elapsedTime = new Date().getTime() - startTime;

            if (elapsedTime <= allowedTime){ // first condition for a swipe met
                // 2nd condition for horizontal swipe met
                if (Math.abs(deltaX) >= threshold &&
                    Math.abs(deltaY) <= restraint) {
                    // if dist traveled is negative, it indicates left swipe
                    swipeDirection = (deltaX < 0) ?
                        this.direction.LEFT :
                        this.direction.RIGHT;
                }
                // 2nd condition for vertical swipe met
                else if (Math.abs(deltaY) >= threshold &&
                         Math.abs(deltaX) <= restraint) {
                    // if dist traveled is negative, it indicates up swipe
                    swipeDirection = (deltaY < 0) ?
                        this.direction.UP :
                        this.direction.DOWN;
                }
            }
            if (swipeDirection != null) {
                var direction = swipeDirection;
                swipeDirection = null;
                callback(direction);
            }
        }.bind(this), false);
    };

    // Determine if standard CustomEvent is supported
    EventUtils.prototype.isCustomEventSupported = function () {
        var customEvent, error, customEventSupported = false;
        if (window.CustomEvent) {
            try {
                // Attempt to create a standard CustomEvent object
                customEvent = new CustomEvent(
                    'testEvent', {
                        detail: {},
                        bubbles: true,
                        cancelable: false}
                );
                // If no exception thrown, this platform supports CustomEvent
                customEventSupported = true;
                // Cleanup
                customEvent = null;
            } catch (error) {
                // Standard CustomEvent is not supported
                customEventSupported = false;
            }
        }
        // Return findings
        return customEventSupported;
    };


    // Return Constructor
    return EventUtils.prototype.constructor;
});

